import { useEffect, useState } from "react";
import { BackendServices } from "services";
import { TaskStatisticsResult } from "services/tasks/models/tasksTypes";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface TaskStatisticsTilesProps {
  useMyStats?: boolean;
}

const TaskStatisticsTiles = ({ useMyStats = false }: TaskStatisticsTilesProps) => {
  const [statistics, setStatistics] = useState<TaskStatisticsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatistics();
  }, [i18n.language, useMyStats]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = useMyStats
        ? await BackendServices.tasksService.getMyTaskStatistics()
        : await BackendServices.tasksService.getTaskStatistics();
      if (!response.hasError && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error("Error fetching task statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const tileStyle = {
    border: 'none',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
  };

  const iconContainerStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.8rem',
  };

  if (loading) {
    return (
      <div className="row mb-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="col-md-4 mb-3">
            <div className="card" style={tileStyle}>
              <div className="card-body text-center p-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="row mb-4">
      {/* Total Tasks Tile */}
      <div className="col-md-4 mb-3">
        <div
          className="card"
          style={tileStyle}
          onClick={() => navigate('/home/tasks')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
          }}
        >
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-2 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                  {t("Dashboard:totalTasks")}
                </p>
                <h2 className="mb-0 fw-bold" style={{ fontSize: '2.5rem', color: '#2c3e50' }}>
                  {statistics?.totalTasks || 0}
                </h2>
              </div>
              <div
                style={{
                  ...iconContainerStyle,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                }}
              >
                <i className="bi bi-kanban"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Completed Tasks Tile */}
      <div className="col-md-4 mb-3">
        <div
          className="card"
          style={tileStyle}
          onClick={() => navigate('/home/completedTasks')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
          }}
        >
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-2 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                  {t("Dashboard:completedTasks")}
                </p>
                <h2 className="mb-0 fw-bold" style={{ fontSize: '2.5rem', color: '#2c3e50' }}>
                  {statistics?.completedTasks || 0}
                </h2>
              </div>
              <div
                style={{
                  ...iconContainerStyle,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                }}
              >
                <i className="bi bi-check-circle-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Tasks Tile */}
      <div className="col-md-4 mb-3">
        <div
          className="card"
          style={tileStyle}
          onClick={() => navigate('/home/tasks')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
          }}
        >
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <p className="text-muted mb-2 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                  {t("Dashboard:pendingTasks")}
                </p>
                <h2 className="mb-0 fw-bold" style={{ fontSize: '2.5rem', color: '#2c3e50' }}>
                  {statistics?.pendingTasks || 0}
                </h2>
              </div>
              <div
                style={{
                  ...iconContainerStyle,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                }}
              >
                <i className="bi bi-hourglass-split"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskStatisticsTiles;
